// src/components/TemplateDuplicatorModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  Badge,
  ListGroup
} from 'react-bootstrap';
import { organizationsAPI, surveysAPI } from '../services/api';

const TemplateDuplicatorModal = ({ 
  show, 
  onHide, 
  template, 
  onSuccess 
}) => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (show) {
      loadOrganizations();
      setSelectedOrgs(new Set());
      setError('');
      setSuccess(null);
    }
  }, [show]);

  const loadOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const response = await organizationsAPI.getOrganizations({
        skip: 0,
        limit: 500,
        only_active: true
      });
      setOrganizations(response.items || []);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError('Не удалось загрузить список организаций');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const toggleOrganization = (orgId) => {
    const newSelected = new Set(selectedOrgs);
    if (newSelected.has(orgId)) {
      newSelected.delete(orgId);
    } else {
      newSelected.add(orgId);
    }
    setSelectedOrgs(newSelected);
  };

  const selectAll = () => {
    const allIds = new Set(organizations.map(org => org.id));
    setSelectedOrgs(allIds);
  };

  const deselectAll = () => {
    setSelectedOrgs(new Set());
  };

  const handleDuplicate = async () => {
    if (selectedOrgs.size === 0) {
      setError('Выберите хотя бы одну организацию');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(null);

    try {
      const result = await surveysAPI.duplicateTemplate(
        template.id,
        Array.from(selectedOrgs)
      );

      setSuccess({
        total: result.total_organizations,
        created: result.created_surveys.length,
        failed: result.failed.length,
        surveys: result.surveys
      });

      if (onSuccess) {
        onSuccess(result);
      }

      // Автоматически закрываем через 3 секунды
      setTimeout(() => {
        onHide();
      }, 3000);

    } catch (err) {
      console.error('Error duplicating template:', err);
      setError(err.response?.data?.detail || 'Ошибка при размножении анкеты');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-files me-2 text-primary"></i>
          Размножение анкеты-шаблона
          {template && (
            <span className="text-muted ms-2">"{template.title}"</span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3">
            <i className="bi bi-check-circle me-2"></i>
            <strong>Успешно!</strong> Создано {success.created} из {success.total} анкет.
            {success.failed > 0 && (
              <div className="mt-2">
                <small>Не удалось создать для {success.failed} организаций</small>
              </div>
            )}
          </Alert>
        )}

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">
              <i className="bi bi-building me-2"></i>
              Выберите организации ({selectedOrgs.size} из {filteredOrganizations.length})
            </h6>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={selectAll}>
                Выбрать все
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={deselectAll}>
                Снять все
              </Button>
            </div>
          </div>

          <Form.Control
            type="text"
            placeholder="Поиск организаций..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-3"
          />

          {loadingOrgs ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Загрузка организаций...</span>
            </div>
          ) : filteredOrganizations.length === 0 ? (
            <Alert variant="info">
              Организации не найдены
            </Alert>
          ) : (
            <div className="border rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <ListGroup variant="flush">
                {filteredOrganizations.map(org => (
                  <ListGroup.Item
                    key={org.id}
                    action
                    onClick={() => toggleOrganization(org.id)}
                    active={selectedOrgs.has(org.id)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className={`bi ${selectedOrgs.has(org.id) ? 'bi-check-square-fill' : 'bi-square'}`}></i>
                      <div>
                        <div className="fw-semibold">{org.name}</div>
                        <small className="text-muted">
                          {org.actual_address?.substring(0, 50)}
                          {org.actual_address?.length > 50 && '...'}
                        </small>
                      </div>
                    </div>
                    <Badge bg="secondary" pill>
                      {org.users_count || 0} пользователей
                    </Badge>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
        </div>

        <Alert variant="info" className="mb-0">
          <i className="bi bi-info-circle me-2"></i>
          <strong>Что произойдет?</strong>
          <ul className="mt-2 mb-0 small">
            <li>Для каждой выбранной организации будет создана отдельная анкета</li>
            <li>Каждая анкета получит уникальный QR-код и публичную ссылку</li>
            <li>Статистика ответов будет собираться отдельно для каждой организации</li>
            <li>Название анкеты будет дополнено названием организации</li>
          </ul>
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Отмена
        </Button>
        <Button 
          variant="primary" 
          onClick={handleDuplicate}
          disabled={loading || selectedOrgs.size === 0}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Создание анкет...
            </>
          ) : (
            <>
              <i className="bi bi-files me-2"></i>
              Создать {selectedOrgs.size} анкет{selectedOrgs.size === 1 ? 'у' : 
                selectedOrgs.size < 5 ? 'ы' : ''}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TemplateDuplicatorModal;